import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormGroup, AbstractControl, ValidatorFn, ValidationErrors, FormControl, FormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  CalcGlulamSpecForm,
  CreateGlulamSpecForm,
} from '@app/core/models/forms/gluelam/glulam-spec/create-glulam-spec-form.model';
import { CreateGlulamSpecRowForm } from '@app/core/models/forms/gluelam/glulam-spec/create-glulam-spec-row-form.model';
import { GlulamSpecFormModel, IProductListItem } from '@app/core/models/gulam-spec.model';
import { getGlueSpecLayerTypesValues, IDropDownDataItem } from '@app/core/models/model-glue-layer-type.enum';
import { AppNotificationService } from '@app/core/services/custom-services/notification.service';
import { EntitySimpleService, IEntitySimpleDTO } from '@app/core/services/http-services/model/entity-simple.service';
import {
  EntityRelationDirection,
  EntityService,
  IEntity,
  IGlulamSpec,
  IGlulamSpecCalcResult,
} from '@app/core/services/http-services/model/entity.service';
import { _, TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { DialogRef, DialogContentBase, DialogTitleBarComponent, DialogActionsComponent } from '@progress/kendo-angular-dialog';
import { SVGIcon, minusIcon, plusIcon, calculatorIcon } from '@progress/kendo-svg-icons';
import { firstValueFrom, forkJoin, Observable, Subject, takeUntil } from 'rxjs';

import { FormFieldComponent, NumericTextBoxComponent, ErrorComponent, SwitchComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { DropDownListComponent, ItemTemplateDirective, ValueTemplateDirective } from '@progress/kendo-angular-dropdowns';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { ConfirmationDialogComponent } from '@app/core/components/confirmation-dialog/confirmation-dialog.component';

_('EntityTranslation.MinStockShouldbeGreaterThanZero');
_('GluePlan.EntityMandatory');
_('GluePlan.NrOfLamellasShouldbeGreaterThanZero');
_('GluePlan.ThicknessShouldbeGreaterThanZero');
_('GluePlan.EntityHasNoThickness');
_('GluePlan.EntityThicknessExceeded');
_('GluePlan.BeamHightMandatory');
_('GluePlan.MoreThanOneHeightDependant');

@Component({
    selector: 'app-glulam-spec',
    templateUrl: './glulam-spec.component.html',
    styleUrls: ['./glulam-spec.component.css'],
    imports: [
    DialogTitleBarComponent,
    TranslateDirective,
    FormsModule,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    NumericTextBoxComponent,
    ErrorComponent,
    DropDownListComponent,
    ItemTemplateDirective,
    ValueTemplateDirective,
    LoaderComponent,
    SwitchComponent,
    ButtonComponent,
    DialogActionsComponent,
    TranslatePipe,
    ConfirmationDialogComponent
],
})
export class GlulamSpecComponent extends DialogContentBase implements OnInit {
  loading = false;
  gluSpecForm: FormGroup<CreateGlulamSpecForm> = null;
  calcgluSpecForm: FormGroup<CalcGlulamSpecForm> = new FormGroup<CalcGlulamSpecForm>({
    planingThickness: new FormControl<number>(40.5, [Validators.min(0)]),
    height: new FormControl<number>(1000, [Validators.required]),
    isBeamMaterialHight: new FormControl<boolean>(false, [Validators.required]),
    useDimensionalAccuracy: new FormControl<boolean>(true, [Validators.required]),
  });
  formdata: GlulamSpecFormModel;
  productList: IProductListItem[] = [];
  glulamSpecs: IGlulamSpec[] = [];
  removedSpecs: IGlulamSpec[] = [];
  layerTypes: Array<IDropDownDataItem>;

  get specRows(): FormArray {
    const specRows = this.gluSpecForm.get('specrows') as FormArray;
    specRows.controls.sort((a, b) => a.value?.sequence - b.value?.sequence);
    return specRows;
  }

  activity: IEntity = null;
  calculating: boolean = false;
  defaultItem: { name: string; valueField: number } = {
    name: 'App.SelectItem',
    valueField: null,
  };
  calcResult: IGlulamSpecCalcResult = null;
  isInvalidSum: boolean = false;

  //Icons
  minusIcon: SVGIcon = minusIcon;
  plusIcon: SVGIcon = plusIcon;
  calculatorIcon: SVGIcon = calculatorIcon;

  private readonly destroy = new Subject<void>();
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  constructor(
    private readonly entityService: EntityService,
    private readonly dialogRef: DialogRef,
    private readonly appNotificationService: AppNotificationService,
    private readonly entitySimpleService: EntitySimpleService
  ) {
    super(dialogRef);
  }

  ngOnInit(): void {
    this.specRows.setValidators(this.onlyOneHightDep);
  }

  newLine(gs: IGlulamSpec): FormGroup<CreateGlulamSpecRowForm> {
    return this.formBuilder.group<CreateGlulamSpecRowForm>({
      activityIndex: this.formBuilder.control(gs.activityIndex),
      productIndex: this.formBuilder.control(gs.productIndex, [Validators.required]),
      sequence: this.formBuilder.control(gs.sequence),
      numberOfLammellas: this.formBuilder.control(gs.numberOfLammellas),
      planingThickness: this.formBuilder.control(gs.planingThickness, [Validators.required]),
      maxThickness: this.formBuilder.control(0),
      heightDependant: this.formBuilder.control(gs.heightDependant),
      dryJoint: this.formBuilder.control(gs.dryJoint),
      turn: this.formBuilder.control(gs.turn),
      isNew: this.formBuilder.control(gs.isNew),
      isChanged: this.formBuilder.control(gs.isChanged),
      isRemoved: this.formBuilder.control(gs.isRemoved),
      layerType: this.formBuilder.control(gs.layerType),
    });
  }

  private onlyOneHightDep: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const formArray = control as FormArray;
    const grouplayertypes = this.groupBy(
      formArray.controls.map((ctrl) => ctrl.value),
      (value) => value?.layerType
    );
    const heightDependantCount = Object.entries(grouplayertypes).map(
      (x) => (x[1] as any[]).filter((a) => a.heightDependant === true && a.layerType == x[0]).length
    );
    return heightDependantCount.find((x) => x > 1) ? { onlyOneHightDep: true } : null;
  };

  async setup(entity: IEntity, form: FormGroup<CreateGlulamSpecForm>) {
    this.gluSpecForm = form;

    this.loading = true;

    this.activity = entity;

    this.layerTypes = getGlueSpecLayerTypesValues();

    const relations = entity.entityRelations.filter(
      (er) => er.direction === EntityRelationDirection.CONSUMES && er.unitCode !== 'HOUR'
    );

    relations.forEach((er) => {
      er.glulamSpec.forEach((g) => {
        this.glulamSpecs.push(g);
      });
      this.glulamSpecs.sort((a, b) => a.sequence - b.sequence);
    });

    const tasks: Observable<IEntitySimpleDTO>[] = [];

    relations.forEach((e) => {
      tasks.push(this.entitySimpleService.get(e.productIndex));
    });

    let products = await firstValueFrom(forkJoin(tasks).pipe(takeUntil(this.destroy)));

    this.productList = products.map((p) => <IProductListItem>{ key: p.index, value: p.description });
    this.calc();
    this.loading = false;
  }

  getMaxThickness(productIndex: number): number {
    const entity = this.productList.find((f) => f.key === productIndex);
    if (entity !== null) {
      return entity.thickness;
    }
    return 0;
  }

  onTurnChange(c: AbstractControl) {
    c.get('isChanged').patchValue(true);
  }

  onProductChange(c: AbstractControl) {
    const pi = c.get('productIndex').value;
    const mv = this.getMaxThickness(pi);
    c.get('maxThickness').patchValue(mv);
    c.get('isChanged').patchValue(true);
  }

  calculateRelation(): Map<number, number> {
    const relations = new Map<number, number>();

    const rows = this.specRows;
    rows.controls.forEach((r) => {
      const pi = r.get('productIndex').value;
      const nl = r.get('numberOfLammellas').value;
      const removed = r.get('isRemoved').value;

      const entity = this.productList.find((f) => f.key === pi);
      const relation = (((entity.width / 1000) * entity.thickness) / 1000) * nl;
      if (relations.has(pi)) {
        if (!removed) {
          relations.set(pi, relations.get(pi) + relation);
        }
      } else {
        relations.set(pi, removed ? 0 : relation);
      }
    });

    return relations;
  }

  onSubmit() {
    if (this.validate() && !this.isInvalidSum) {
      this.closeDialog();
    }
  }

  confirmSubmit(isSubmit: boolean) {
    if (isSubmit) {
      this.closeDialog();
    }
    this.isInvalidSum = null;
  }

  onCancel() {
    this.dialogRef.close(false);
  }

  validate(): boolean {
    let valid = true;
    if (!this.gluSpecForm.valid) {
      const messages = this.gluSpecForm?.errors?.['errormessages'];
      messages.forEach((e) => {
        this.showMessage(e);
      });
      valid = false;
    }

    this.ValidateSumSmallerThanMinOrGreaterThanMax();

    return valid;
  }

  showMessage(message: string) {
    this.appNotificationService.notifyErrorAppChanel(message);
  }

  removeRow(control: AbstractControl) {
    const rows = this.specRows;
    const actualIndex = rows.controls.indexOf(control as any);

    if (actualIndex === -1) return;

    // If it's an existing row, track it for deletion
    if (!control.get('isNew').value) {
      const specToRemove = { ...control.value, isRemoved: true };
      this.removedSpecs.push(specToRemove);
    }

    // Always physically remove from the form array
    rows.removeAt(actualIndex);
  }

  getActiveLines() {
    const specRows = this.specRows.controls
      .filter((c) => !c.value?.isRemoved)
      .sort((a, b) => a.value?.sequence - b.value?.sequence);
    return specRows;
  }

  addRow() {
    const rows = this.specRows;

    // Calculate max sequence from both active AND removed specs to ensure uniqueness
    const activeMaxSequence = Math.max(...rows.controls.map((o) => o.get('sequence').value), 0);
    const removedMaxSequence = Math.max(...this.removedSpecs.map((s) => s.sequence), 0);
    const maxSequenceValue = Math.max(activeMaxSequence, removedMaxSequence);

    rows.push(
      this.newLine(<IGlulamSpec>{
        activityIndex: this.activity?.index,
        heightDependant: false,
        numberOfLammellas: null,
        planingThickness: 0,
        maxThickness: 0,
        productIndex: null,
        sequence: maxSequenceValue + 1,
        dryJoint: false,
        turn: false,
        isNew: true,
        isChanged: false,
        isRemoved: false,
        layerType: null,
      })
    );
  }

  calc() {
    this.calculating = true;

    this.entityService
      .calcGluSpec(
        this.activity.index,
        this.calcgluSpecForm.value.planingThickness,
        this.calcgluSpecForm.value.height,
        this.calcgluSpecForm.value.isBeamMaterialHight,
        this.calcgluSpecForm.value.useDimensionalAccuracy,
      )
      .subscribe((calcResult) => {
        for (let g of calcResult.lams) {
          let line = this.glulamSpecs.find((l) => g.sequence === l.sequence);
          line.numberOfLammellasCalc = g.numberOfLammellas;
        }
        this.calcResult = calcResult;
        this.calculating = false;
      });
  }

  private closeDialog() {
    // Temporarily add removed specs back to the form array so they're included in the form value
    this.removedSpecs.forEach(spec => {
      this.specRows.push(this.newLine(spec));
    });

    this.dialogRef.close({ success: true, relations: this.calculateRelation(), glulamspec: this.gluSpecForm });
  }

  private ValidateSumSmallerThanMinOrGreaterThanMax() {
    let sum = 0;
    this.specRows.value.forEach((element) => {
      sum += element?.numberOfLammellas * element?.planingThickness;
    });

    if (sum < this.gluSpecForm.value.hightMin || sum > this.gluSpecForm.value.hightMax) {
      this.isInvalidSum = true;
    } else {
      this.isInvalidSum = false;
    }
  }

  private groupBy(array, key) {
    return array.reduce((result, item) => {
      const groupKey = item[key];
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(item);
      return result;
    }, {});
  }
}
